import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { useActiveStatusMatchCampaigns } from 'loyalty/hooks/useActiveStatusMatchCampaign'
import { LOUNGE_PASSES_PAGE, LOYALTY_LANDING_PAGE, LOYALTY_OFFERS_HUB_PAGE, LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import SocieteNavBarMenuButton from './SocieteNavBarMenuButton'
import SocieteNavBarMenuDrawer from './SocieteNavBarMenuDrawer'

// const SOCIETE_CREDIT_CARD_URL = 'https://luxuryescapes.com/au/societe/credit-card'
const SOCIETE_REWARDS_PATH = '/societe/rewards-seats-maldives'
const STATUS_MATCH_CONFIG_BY_REGION: Record<string, { path: string; slug: string }> = {
  US: { path: '/societe/status-match-us-2026', slug: 'status-match-us-2026' },
  HK: { path: '/societe/status-match-hk-2026', slug: 'status-match-hk-2026' },
  NZ: { path: '/societe/status-match-nz-2026', slug: 'status-match-nz-2026' },
  SG: { path: '/societe/status-match-sg-2026', slug: 'status-match-sg-2026' },
  DE: { path: '/societe/status-match-de-2026', slug: 'status-match-de-2026' },
}
const SOCIETE_NAV_BAR_DESKTOP_HEIGHT = 48

interface SocieteNavLink {
  label: string
  to?: string
  href?: string
}

interface SocieteNavMenu {
  label: string
  items: Array<SocieteNavLink>
}

const Bar = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  padding: 0;

  &.disable-sticky {
    position: relative;
  }
`

const DesktopNavContainer = styled(LayoutContainer)`
  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)};
  }
`

const ButtonsContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${rem(4)};
  align-items: center;
  justify-content: flex-start;
`

const MenuButton = styled(TextButton)`
  && {
    height: ${rem(SOCIETE_NAV_BAR_DESKTOP_HEIGHT)};
    padding: ${rem(0)} ${rem(10)};
    text-decoration: none;
    flex: 0 0 auto;
    width: auto;

    span {
      border-bottom: none;
    }
  }
`

const ChipsWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: ${rem(48)};
    background: linear-gradient(to left, ${props => props.theme.palette.neutral.surface.normalBackground}, transparent);
    pointer-events: none;
  }
`

const ChipsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(12)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

function SocieteNavBar() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const activeStatusMatchCampaigns = useActiveStatusMatchCampaigns()
  const barRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(barRef, 'sticky')

  const benefitsMenu = useMemo<SocieteNavMenu>(() => {
    const tierName = getLoyaltyTierDisplayName(tier)
    const programName = getLoyaltyProgramDisplayName()
    let allBenefitsLabel = intl.formatMessage({
      id: 'TzO1hnnPew0LEnzvoM9P',
      defaultMessage: '{programName} Benefits',
      description: 'Dropdown item label for loyalty program benefits in the Societe tertiary navigation menu',
    }, { programName })

    if (isLoggedIn) {
      allBenefitsLabel = intl.formatMessage({
        id: 'Y4lB1Td_gl2-3p3erjHH',
        defaultMessage: 'All {tierName} Benefits',
        description: 'Dropdown item label for all benefits in the Societe tertiary navigation menu',
      }, { tierName })
    }

    return {
      label: intl.formatMessage({
        id: 'gKOCxXyhQq96WU_pxHH0',
        defaultMessage: 'Benefits',
        description: 'Top-level dropdown label for Benefits in the Societe tertiary navigation menu',
      }),
      items: [
        {
          label: allBenefitsLabel,
          // Infinite has no "about" benefits table — send them to the benefits tiles on their membership page.
          to: `/${LOYALTY_LANDING_PAGE}#${isInfiniteTier ? 'benefits-your-tier' : 'about-benefits'}`,
        },
        {
          label: intl.formatMessage({
            id: 'mX61fJmQgKKdEM-KHkQZ',
            defaultMessage: 'Airport Lounges',
            description: 'Dropdown item label for airport lounges in the Societe tertiary navigation menu',
          }),
          to: `/${LOUNGE_PASSES_PAGE}`,
        },
      ],
    }
  }, [intl, isLoggedIn, tier, isInfiniteTier])

  const pointsMenu = useMemo<SocieteNavMenu>(() => ({
    label: intl.formatMessage({
      id: 'yU8mM2OqRw1kfc-BbySW',
      defaultMessage: 'Points',
      description: 'Top-level dropdown label for Points in the Societe tertiary navigation menu',
    }),
    items: [
      ...(currentRegionCode === 'AU' ? [{
        label: intl.formatMessage({
          id: 'ZrnjA-P3SVBGFI_jZRId',
          defaultMessage: 'Société Rewards',
          description: 'Dropdown item label for Societe Rewards in the Societe tertiary navigation menu',
        }),
        to: SOCIETE_REWARDS_PATH,
      }] : []),
      {
        label: intl.formatMessage({
          id: 'OIoiRm70YOrOS-0XaT3C',
          defaultMessage: 'Top up Points',
          description: 'Dropdown item label for buying more points in the Societe tertiary navigation menu',
        }),
        to: `/${LOYALTY_PURCHASE_POINTS_PAGE}`,
      },
      // Enable after LOYAL-2371 ships the Spend my Points page.
      // {
      //   label: intl.formatMessage({
      //     id: 'iZICWtuUzG11dQXKUsD9',
      //     defaultMessage: 'Spend my Points',
      //     description: 'Dropdown item label for spending points in the Societe tertiary navigation menu',
      //   }),
      //   to: `/${LOYALTY_SPEND_POINTS_PAGE}`,
      // },
    ],
  }), [currentRegionCode, intl])

  const navLinks = useMemo<{
    membershipLink?: SocieteNavLink
    aboutLink?: SocieteNavLink
    offerLink: SocieteNavLink
    statusMatchLink?: SocieteNavLink
  }>(() => {
    const statusMatchConfig = STATUS_MATCH_CONFIG_BY_REGION[currentRegionCode]
    const hasActiveStatusMatchCampaign = !!statusMatchConfig && activeStatusMatchCampaigns.some(
      (campaign) => campaign.config.slug === statusMatchConfig.slug,
    )

    const membershipLink = isLoggedIn ? {
      label: intl.formatMessage({
        id: 'A58hHJjGnrUe-PFDqNKJ',
        defaultMessage: 'Your Membership',
        description: 'Top-level link label in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_LANDING_PAGE}`,
    } : undefined

    // Infinite members have no "About Société" section (its content is gated off for them on the
    // account page), so omit the tertiary-nav link too.
    const aboutLink = isInfiniteTier ? undefined : {
      label: intl.formatMessage({
        id: 'ghD8njE0B2cnhg69mlSq',
        defaultMessage: 'About Société',
        description: 'Top-level link label for the about Société section in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_LANDING_PAGE}#about`,
    }

    // Enable after the Société Credit Card page is ready.
    // const creditCardLink = currentRegionCode === 'AU' ? {
    //   label: intl.formatMessage({
    //     id: 'GeECXpTBwmzXzLE1PoxG',
    //     defaultMessage: 'Société Credit Card',
    //     description: 'Top-level link label for the Societe credit card page in the Societe tertiary navigation menu',
    //   }),
    //   href: SOCIETE_CREDIT_CARD_URL,
    // } : undefined

    const offerLink = {
      label: intl.formatMessage({
        id: 'WSZ9W_wkwPRyNU4CJ5s-',
        defaultMessage: 'Offers',
        description: 'Top-level link label for the Societe offers hub in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_OFFERS_HUB_PAGE}`,
    }

    const statusMatchLink = statusMatchConfig && hasActiveStatusMatchCampaign ? {
      label: intl.formatMessage({
        id: 'y15_icuBYSRt21kwwSyL',
        defaultMessage: 'Status Match',
        description: 'Top-level link label for Status Match in the Societe tertiary navigation menu',
      }),
      to: statusMatchConfig.path,
    } : undefined

    return {
      membershipLink,
      aboutLink,
      offerLink,
      statusMatchLink,
    }
  }, [activeStatusMatchCampaigns, currentRegionCode, intl, isLoggedIn, isInfiniteTier])

  const openBenefitsDrawer = useCallback(() => {
    showModal(<SocieteNavBarMenuDrawer {...benefitsMenu} />)
  }, [benefitsMenu, showModal])

  const openPointsDrawer = useCallback(() => {
    showModal(<SocieteNavBarMenuDrawer {...pointsMenu} />)
  }, [pointsMenu, showModal])

  const {
    membershipLink,
    aboutLink,
    offerLink,
    statusMatchLink,
  } = navLinks

  return <Bar background="normal-background" ref={barRef}>
    <CSSBreakpoint min="desktop">
      <DesktopNavContainer variant="extraLarge">
        <ButtonsContainer>
          {membershipLink && <MenuButton
            kind="tertiary"
            variant="default"
            size="medium"
            to={membershipLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {membershipLink.label}
            </Group>
          </MenuButton>}
          {aboutLink && <MenuButton
            kind="tertiary"
            variant="default"
            size="medium"
            to={aboutLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {aboutLink.label}
            </Group>
          </MenuButton>}
          <SocieteNavBarMenuButton {...benefitsMenu} />
          <SocieteNavBarMenuButton {...pointsMenu} />
          <MenuButton
            kind="tertiary"
            variant="default"
            size="medium"
            to={offerLink.to}
            href={offerLink.href}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {offerLink.label}
            </Group>
          </MenuButton>
          {statusMatchLink && <MenuButton
            kind="tertiary"
            variant="default"
            size="medium"
            to={statusMatchLink.to}
            href={statusMatchLink.href}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {statusMatchLink.label}
            </Group>
          </MenuButton>}
        </ButtonsContainer>
      </DesktopNavContainer>
    </CSSBreakpoint>

    <CSSBreakpoint max="tablet">
      <ChipsWrapper>
        <ChipsContainer>
          {membershipLink && <AssistChip
            variant="filled"
            size="small"
            to={membershipLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {membershipLink.label}
            </Group>
          </AssistChip>}
          {aboutLink && <AssistChip
            variant="filled"
            size="small"
            to={aboutLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {aboutLink.label}
            </Group>
          </AssistChip>}
          <AssistChip
            variant="filled"
            size="small"
            onClick={openBenefitsDrawer}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {benefitsMenu.label}
              <LineAngleDownIcon size={16} colour="normal-contrast" />
            </Group>
          </AssistChip>
          <AssistChip
            variant="filled"
            size="small"
            onClick={openPointsDrawer}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {pointsMenu.label}
              <LineAngleDownIcon size={16} colour="normal-contrast" />
            </Group>
          </AssistChip>
          <AssistChip
            variant="filled"
            size="small"
            to={offerLink.to}
            href={offerLink.href}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {offerLink.label}
            </Group>
          </AssistChip>
          {statusMatchLink && <AssistChip
            variant="filled"
            size="small"
            to={statusMatchLink.to}
            href={statusMatchLink.href}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {statusMatchLink.label}
            </Group>
          </AssistChip>}
        </ChipsContainer>
      </ChipsWrapper>
    </CSSBreakpoint>
  </Bar>
}

export default SocieteNavBar
